import { ComponentConfig } from "@measured/puck";
import Head from "next/head";
import { NextSeo } from "next-seo";
import {
  getCmsApiClientFunctions,
  IContent,
} from "@kunduz/data-access-cms-api";
import classNames from "classnames";
import styles from "./root.module.scss";
import { getCachedHeaderBanner } from "../../../../src/common/services/cachedHeaderBanner";
import { getCookie } from "cookies-next";
import {
  HeaderComponent,
  WebsiteEnFooter,
  WebsiteTrFooter,
} from "@kunduz/feature-landing-pages";

export interface RootProps {
  meta?: {
    seo_title?: string;
    seo_desc?: string;
    canonical_url?: string;
    no_index?: boolean;
    seo_icon?: string;
    slug?: string;
    name?: string;
  };
  headerContactOptionsShow: boolean;
  headerBannerShow: boolean;
  headerBannerData?: IContent.HeaderBannerResponse;
  live: boolean;
  headerProfile?: number;
  language?: "tr" | "en";
  headerItems?: IContent.HeaderItemsResponse;
}

export const Root: ComponentConfig<RootProps> = {
  fields: {
    live: {
      label: "Live",
      type: "radio",
      options: [
        { label: "Enabled", value: true },
        { label: "Disabled", value: false },
      ],
    },
    language: {
      type: "radio",
      label: "Language",
      options: [
        { label: "Turkish", value: "tr" },
        { label: "English", value: "en" },
      ],
    },
    headerProfile: {
      type: "select",
      label: "Header Profile",
      options: [
        { label: "Student", value: "student" },
        { label: "School", value: "school" },
        { label: "Teacher", value: "teacher" },
      ],
    },
    headerBannerShow: {
      label: "Banner Show",
      type: "radio",
      options: [
        { label: "Yes", value: true },
        { label: "No", value: false },
      ],
    },
    headerContactOptionsShow: {
      label: "Contact Options Show",
      type: "radio",
      options: [
        { label: "Yes", value: true },
        { label: "No", value: false },
      ],
    },
    meta: {
      type: "object",
      objectFields: {
        canonical_url: {
          type: "text",
          label: "Canonical URL",
        },
        seo_title: {
          type: "text",
          label: "SEO Title",
        },
        seo_desc: {
          type: "text",
          label: "SEO Description",
        },
        no_index: {
          type: "radio",
          label: "No Index",
          options: [
            { label: "Yes", value: true },
            { label: "No", value: false },
          ],
        },
        seo_icon: {
          type: "text",
          label: "SEO Icon",
        },
        slug: {
          type: "text",
          label: "Slug",
        },
        name: {
          type: "text",
          label: "Name",
        },
      },
    },
  },
  resolveData: async ({ props }) => {
    const { language, headerProfile } = props;

    const puckToken = getCookie("kz_puck_token") as string;

    const cmsClient = getCmsApiClientFunctions({ token: puckToken });

    const [headerItems, headerBannerData] = await Promise.all([
      cmsClient.getHeaderItemById(headerProfile),
      getCachedHeaderBanner(language),
    ]);

    return {
      props: {
        ...props,
        headerItems,
        headerBannerData,
      },
      readOnly: {
        headerItems: true,
        headerBannerData: true,
      },
    };
  },
  resolveFields: async (data, params) => {
    const puckToken = getCookie("kz_puck_token") as string;

    const cmsClient = getCmsApiClientFunctions({ token: puckToken });

    const [headerProfiles] = await Promise.all([cmsClient.getHeaderProfiles()]);

    const field: any = {
      type: "select",
      label: "Header Profile",
      options: headerProfiles?.map((profile) => ({
        label: profile.name,
        value: profile.id,
      })),
    };

    return { ...params.fields, headerProfile: field };
  },
  defaultProps: {
    live: false,
    headerProfile: 1,
    language: "tr",
    headerBannerShow: true,
    headerContactOptionsShow: true,
  },
  render: ({ puck: { renderDropZone: DropZone, isEditing }, ...props }) => {
    const {
      meta,
      headerBannerData,
      language,
      headerItems,
      headerBannerShow,
      headerContactOptionsShow,
    } = props;

    return (
      <>
        <Head>
          <link rel="preconnect" href="https://media.kunduz.com" />
        </Head>

        <NextSeo
          title={meta?.seo_title}
          description={meta?.seo_desc}
          canonical={meta?.canonical_url}
          noindex={meta?.no_index}
          openGraph={{
            images: [{ url: meta?.seo_icon as string }],
          }}
        />

        <div
          className={classNames("puck-root", { [styles.isEditing]: isEditing })}
        >
          <HeaderComponent
            language={language}
            headerContactShow={headerContactOptionsShow}
            headerContactData={headerItems?.top_item}
            headerBannerShow={headerBannerShow}
            headerBannerData={headerBannerData}
            headerShow={!!headerItems?.profile}
            headerItems={headerItems}
            isEditing={isEditing}
          />

          <DropZone
            zone="default-zone"
            style={{ flexGrow: 1, minHeight: "100vh" }}
          />

          {language === "tr" && <WebsiteTrFooter />}
          {language === "en" && <WebsiteEnFooter />}
        </div>
      </>
    );
  },
};

export default Root;
